import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Tooltip, Typography, InputAdornment, Stack, Grid, useTheme, Dialog, Drawer } from '@mui/material';
import { Link, useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import StatusChip from 'components/Table/StatusChip';
import DataInfoSecondary from 'components/Widgets/DataInfoSecondary';
import DataInfoColorWidget from 'components/Widgets/DataInfoColorWidget';
import { colorsDark } from 'assets/theme/colorUtil';
import { ReactComponent as AddIcon } from '../../assets/plus-icon.svg';
import { ReactComponent as EditIcon } from '../../assets/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import { ReactComponent as ClockIcon } from '../../assets/time-history.svg';
import bulkUploadIcon from '../../assets/hugeicons_file-upload.png';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import './dataPipeline.scss';
import moment from 'moment-timezone';
import { CustomizedDataGrid } from '../customized_data_grid';
import { ReactComponent as InfoGreyIcon } from '../../assets/icon-grey-info.svg';
import CustomTextField from 'components/Inputs/CustomTextField';
import { Search } from '@mui/icons-material';
import RowTypography from 'components/Table/RowTypography';
import { calculateRange } from 'components/Dropdown/DateTimeRangeFilter/useDateTimeRangePicker';
import { convertMsToHHMMSS, dependInfoTransformData, formatExecutionTime, TIMEZONE_MAP } from 'utils/common-util';
import { exportIbpJobExecution, getStageKpi, getIbpJobExecutionData, getDependentJobExeData, postJobComment } from 'service/service';
import { PLATFORM_ID_TO_NAME } from 'components/DataPipeline/constant';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import { Column } from 'react-data-grid';
import RagBar from 'components/Table/RagBar';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { checkRole } from 'utils/roleUtils';
import { DataPipelineModal } from './modal-component';
import CyclicExecutions from './CyclicExecutions';
import Loader from 'components/Loader/loader';
import TeradataPlatformIcon from '../../assets/Teradata_Platform.svg';
import BODSPlatformIcon from '../../assets/BODS_Platform.svg';
import DDHPlatformIcon from '../../assets/DDH_Platform.svg';
import AzureDataFactoryPlatformIcon from '../../assets/AzureDataFactory_Platform.svg';
import MDTPlatformIcon from '../../assets/MDT_Platform.svg';
import WindowsServerPlatformIcon from '../../assets/WindowsServer_Platform.svg';
import SAPBWPlatformIcon from '../../assets/SAP_Platform.svg';
import SQLPlatformIcon from '../../assets/SQL_Platform.svg';
import ADBPlatformIcon from '../../assets/ADB_Platform.svg';

export const triggerNameFormatter = () => {
    return ({ row }: any) => {
      const triggerName =
        row.triggerName && row.triggerName !== 'manual' && row.triggerName !== 'all'
          ? row.triggerName
          : '--';
  
      return <RowTypography isSuccessor={!row.hasDependent}>{triggerName}</RowTypography>;
    };
  };

  export const dataAssetNameFormatter = () => {
  return ({ row }: any) => {
    const dataAssetName = row.dataAssetName?.trim() ?? '--';

    return <RowTypography isSuccessor={!row.hasDependent}>{dataAssetName}</RowTypography>;
  };
};

export const silverTableNameFormatter = () => {
  return ({ row }: any) => {
    const silverTableName = row.silverTableName?.trim() ?? '--';

    return <RowTypography isSuccessor={!row.hasDependent}>{silverTableName}</RowTypography>;
  };
};

export const folderNamesFormatter = () => {
  return ({ row }: any) => {
    const folderNames = row.folderNames?.trim() ?? '--';

    return <RowTypography isSuccessor={!row.hasDependent}>{folderNames}</RowTypography>;
  };
};

export const subFolderNamesFormatter = () => {
  return ({ row }: any) => {
    const subFolderNames = row.subFolderNames?.trim() ?? '--';

    return <RowTypography isSuccessor={!row.hasDependent}>{subFolderNames}</RowTypography>;
  };
};

  export const jobRunIdFormatter = () => {
    return ({ row }: any) => {
      const jobRunId = row.jobRunId || '--';
      const hasLink = Boolean(row.adfUrl);
      const isSuccessor = !row.hasDependent;

      const typography = (
        <RowTypography isSuccessor={isSuccessor} title={jobRunId}>
          {jobRunId}
        </RowTypography>
      );

      if (!hasLink) {
        return typography;
      }

      return (
        <Link
          to={row.adfUrl}
          target="_blank"
          state={row}
          style={{
            textDecoration: 'underline',
            ...(row.hasDependent ? {} : { color: '#BCBCBC' }),
          }}
        >
          {typography}
        </Link>
      );
    };
  };

  export const slaStatusFormatter = () => {
    return ({ row }: any) => {
      const slaStatus = row.slaStatus || '--';

      if (slaStatus === '--' || slaStatus === 'NA' || slaStatus === 'N/A') {
        return <RowTypography isSuccessor={!row.hasDependent}>{slaStatus}</RowTypography>;
      }

      const normalized = String(slaStatus).toLowerCase();
      let chipStyle = { backgroundColor: '#B0B7C3', color: '#FFFFFF' };
      if (normalized === 'sla met')
        chipStyle = { backgroundColor: 'rgba(1, 192, 8, 1)', color: '#FFFFFF' };       // green (matches Success)
      else if (normalized === 'sla missed')
        chipStyle = { backgroundColor: 'rgba(189, 29, 64, 1)', color: '#FFFFFF' };      // red (matches Failed)
      else if (normalized === 'in progress')
        chipStyle = { backgroundColor: 'rgba(236, 185, 20, 1)', color: '#FFFFFF' };      // amber (matches In Progress)
      else if (normalized === 'not scheduled' || normalized === 'not started' || normalized === 'scheduled')
        chipStyle = { backgroundColor: 'rgba(169, 173, 203, 1)', color: '#FFFFFF' };    // grey (matches Scheduled)

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '24px',
            px: 1.5,
            py: 0.5,
            borderRadius: '4px',
            backgroundColor: chipStyle.backgroundColor,
            color: chipStyle.color,
            fontWeight: 700,
            fontSize: '0.68rem',
            lineHeight: 1.2,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}
        >
          {slaStatus}
        </Box>
      );
    };
  };

  export const scheduleInfoFormatter = () => {
    return ({ row }: any) => {
      const scheduleInfo = row.scheduleInfo || '--';
  
      return <RowTypography isSuccessor={!row.hasDependent}>{scheduleInfo}</RowTypography>;
    };
  };

  type EditableCellProps = {
    jobId: any;
    jobExeId: any;
    projectId: any;
    comment: string;
    platformId: any;
    isAllowed: boolean;
    textColor: any;
    rowData: any;
    onSave: (
      text: string,
      jobId: any,
      jobExeId: any,
      projectId: any,
      platformId: any,
      rowData: any,
    ) => Promise<void>;
  };
  
  const EditableCell = ({
    jobId,
    jobExeId,
    projectId,
    comment,
    platformId,
    isAllowed,
    textColor,
    onSave,
    rowData
  }: EditableCellProps) => {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(comment ?? '');
  
    const displayText =
      text.length > 20 ? `${text.substring(0, 20)}...` : text;
  
    const isEditableStatus =
      rowData?.jobStatus === 'Success' || 
      rowData?.jobStatus === 'Failed' ||
      rowData?.jobStatus === 'Successful' ||
      rowData?.jobStatus === 'Failure' ||
      rowData?.jobStatus?.toLowerCase() === 'succeeded';
  
    const handleEditClick = () => {
      setEditing(true);
    };
  
    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return;
  
      setEditing(false);
      await onSave(text, jobId, jobExeId, projectId, platformId, rowData);
    };
  
    return (
      <Stack direction="row" alignItems="center" height="100%" justifyContent="flex-end">
        {editing ? (
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write here..."
            style={{
              flexGrow: 1,
              textAlign: "left",
              paddingLeft: 1,
              background: "#003771",
              color: "white",
            }}
            onBlur={() => setEditing(false)}
            disabled={!rowData?.actualStartTime || !isEditableStatus}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <span
            style={{
              flexGrow: 1,
              textAlign: "left",
              paddingLeft: 5,
              color: `${textColor} !important`,
              opacity: text ? 1 : 0.6,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "150px",
              display: "inline-block",
            }}
            title={text}
          >
            {text ? displayText : 'Write here...'}
          </span>
        )}
  
        {isAllowed && (
          <EditIcon
            style={{ cursor: 'pointer' }}
            onClick={handleEditClick}
          />
        )}
      </Stack>
    );
  };

  export const actualStartTimeFormatter = () => {
    return ({ row }: any) => {
      const startDate = row.actualStartTime;
      const isValidStatus = row.jobStatus !== 'Scheduled' && row.jobStatus !== 'Not Scheduled';
  
      return (
        <RowTypography isSuccessor={!row.hasDependent}>
          {' '}
          {startDate && startDate != null && isValidStatus
            ? moment(startDate).tz('America/Chicago').format('MM-DD-YY HH:mm:ss')
            : '--'}{' '}
        </RowTypography>
      );
    };
  };

  export const actualStartTimeFormatterCustom = () => {
    return ({ row }: any) => {
      const startDate = row.actualStartTime;
  
      return (
        <RowTypography isSuccessor={!row.dependentData}>
          {' '}
          {startDate && startDate != null
            ? moment(startDate).tz('America/Chicago').format('MM-DD-YY HH:mm:ss')
            : '--'}{' '}
        </RowTypography>
      );
    };
  };

  export const actualEndTimeFormatter = () => {
    return ({ row }: any) => {
      const endDate = row.actualEndTime;
      const isValidStatus =
        row.jobStatus !== 'Scheduled' &&
        row.jobStatus !== 'Not Scheduled' &&
        row.jobStatus !== 'Long Running' &&
        row.jobStatus !== 'Failed To Start' &&
        row.jobStatus !== 'Delayed';
  
      return (
        <RowTypography isSuccessor={!row.hasDependent}>
          {' '}
          {endDate && isValidStatus
            ? moment(endDate).tz('America/Chicago').format('MM-DD-YY HH:mm:ss')
            : '--'}{' '}
        </RowTypography>
      );
    };
  };

  export const actualEndTimeFormatterCustom = () => {
    return ({ row }: any) => {
      const endDate = row.actualEndTime;
  
      return (
        <RowTypography isSuccessor={!row.dependentData}>
          {' '}
          {endDate ? moment(endDate).tz('America/Chicago').format('MM-DD-YY HH:mm:ss') : '--'}{' '}
        </RowTypography>
      );
    };
  };

  export const avgRunTimeFormatter = () => {
    return ({ row }: any) => {
      const hasAvgRunTime = Boolean(row.avgRunTime);
      const isValidStatus =
        row.jobStatus !== 'Scheduled' &&
        row.jobStatus !== 'Not Scheduled';
  
      return (
        <RowTypography isSuccessor={!row.hasDependent}>
          {hasAvgRunTime && isValidStatus
            ? convertMsToHHMMSS(Number(row.avgRunTime))
            : '--'}
        </RowTypography>
      );
    };
  };

  export const avgRunTimeFormatterCustom = () => {
    return ({ row }: any) => {
      const avgRunTime = row.avgRunTime;
  
      return (
        <RowTypography isSuccessor={!row.dependentData}>
          {avgRunTime
            ? convertMsToHHMMSS(Number(avgRunTime))
            : '--'}
        </RowTypography>
      );
    };
  };

  export const totalExecutionTimeFormatter = () => {
    return ({ row }: any) => {
      return (
        <RowTypography isSuccessor={!row.hasDependent}>{formatExecutionTime(row)}</RowTypography>
      );
    };
  };

  export const totalExecutionsFormatter = (fetchCyclicExecutions: (row: any) => void) => {
    return ({ row }: any) => {
      if (!row.totalExecutions) {
        return <div style={{ textAlign: 'center' }}>--</div>;
      }
  
  
  
      const isRunnable =
        row.jobStatus !== 'Scheduled' && row.jobStatus !== 'Not Scheduled' && row.totalExecutions > 1 && !row.isPredecessor;
  
      const exeValue = row.totalExecutions ? row.totalExecutions : '--';
      const displayValue =
        row.jobStatus === 'Not Scheduled' ||
          row.jobStatus === 'Scheduled' ||
          row.jobStatus === 'Failed To Start'
          ? 0
          : exeValue;
  
      return (
        <Grid
          container
          spacing={1}
          onClick={isRunnable ? () => fetchCyclicExecutions(row) : undefined}
          style={{ cursor: row.totalExecutions > 1 && !row.isPredecessor ? 'pointer' : 'default' }}
        >
          <Grid item xs={6}>
            <StatusChip
              displayNumber
              columnName="totalExecutions"
              status="IP_Delayed_Start"
              value={displayValue}
            />
          </Grid>
  
          <Grid item xs={4} />
  
          {isRunnable && (
            <Grid
              item
              xs={2}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <ClockIcon onClick={() => fetchCyclicExecutions(row)} />
            </Grid>
          )}
        </Grid>
      );
    };
  };

  export const totalExecutionsFormatterCustom = (fetchCyclicExecutions: (row: any) => void) => {
    return ({ row }: any) => {
      if (!row.totalExecutions) {
        return <div style={{ textAlign: 'center' }}>--</div>;
      }
  
      const isClickable = row.totalExecutions > 1 && !row.isPredecessor;
  
      const exeValue = row.totalExecutions ? row.totalExecutions : '--';
      const displayValue =
        row.jobStatus === 'Not Scheduled' ||
          row.jobStatus === 'Scheduled' ||
          row.jobStatus === 'Failed To Start'
          ? 0
          : exeValue;
  
      return (
        <Grid
          container
          spacing={1}
          onClick={isClickable ? () => fetchCyclicExecutions(row) : undefined}
          style={{ cursor: isClickable && !row.isPredecessor ? 'pointer' : 'default' }}
        >
          <Grid item xs={6}>
            <StatusChip
              displayNumber
              columnName="totalExecutions"
              status="IP_Delayed_Start"
              value={displayValue}
            />
          </Grid>
  
          <Grid item xs={4} />
  
          {isClickable && (
            <Grid
              item
              xs={2}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <ClockIcon onClick={() => fetchCyclicExecutions(row)} />
            </Grid>
          )}
        </Grid>
      );
    };
  };

  export const ragBarFormatter = () => {
    return ({ row }: any) => {
      const execStatusTrend = row.execStatusTrend ?? '';
  
      return <RagBar colors={getColorData(execStatusTrend)} />;
    };
  };

  export const orderDateFormatter = () => {
    return ({ row }: any) => {
      const startDate = row.orderDate;
  
      return (
        <RowTypography isSuccessor={!row.dependentData}>
          {' '}
          {startDate ? moment(startDate).utc().format('MM-DD-YY') : '--'}{' '}
        </RowTypography>
      );
    };
  };
  
  export const isCyclicFormatter = () => {
    return ({ row }: any) => {
      let cyclicValue = row.isCyclic;
  
      if (row.isCyclic === '0') {
        cyclicValue = 'No';
      } else if (row.isCyclic === '1') {
        cyclicValue = 'Yes';
      }
  
      return <RowTypography isSuccessor={!row.dependentData}>{cyclicValue}</RowTypography>;
    };
  };

  const getColorData = (data: string) => {
  let colorArr: string[] = [];
  const arr = data.split(',').map(Number);

  arr.forEach((value: number) => {
    switch (value) {
      case 1:
        colorArr.push('rgba(1, 192, 8, 1)'); // Success Color
        break;
      case 2:
        colorArr.push('rgba(216, 43, 81, 1)'); // Failed color
        break;
      case 3:
        colorArr.push('rgba(236, 185, 20, 1)'); // Inprogress Color
        break;
      case 4:
        colorArr.push('rgba(253, 149, 50, 1)'); // LongRunning Color
        break;
      case 5:
        colorArr.push('rgba(255, 56, 176, 1)'); // Delayed Color
        break;
      case 7:
        colorArr.push('rgba(255, 118, 118, 1)'); // Scheduled Color
        break;
      default:
        break;
    }
  });

  return colorArr;
};

  export const jobConditionFormatter = () => {
    return ({ row }: any) => {
      let condition = '--';
  
      if (row.jobCondition === 'A') {
        condition = 'AND';
      } else if (row.jobCondition === 'O') {
        condition = 'OR';
      }
  
      return <RowTypography isSuccessor={!row.hasDependent}>{condition}</RowTypography>;
    };
  };

const StageDetails = () => {
  const theme = useTheme();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  interface Row {
    id: number;
    platformId: number;
    jobName?: string;
    actualStartTime?: string;
    actualEndTime?: string;
    avgRunTime?: any;
    nextExecutionTime?: string;
    jobStatus?: string;
    totalExecutions?: number;
    triggerName?: string;
    pipeline_trigger_id?: string;
    jobRunId?: string;
    jobType?: any;
    orderDate?: any;
    isCyclic?: any;
    jobCondition? : string;
    jobOwner? : string;
    scheduleInfo? : string
    schedulerName? : string;
  }

  const gdoId = params.gdoId || '';
  const gdoName = decodeURIComponent(params.gdoName || 'GDO');
  const stageName = decodeURIComponent(params.stageName || 'Stage');
  const projectId = params.stageId || '';

  const startingDate = searchParams.get('startingDate') || '2026-08-03';
  const endingDate = searchParams.get('endingDate') || '2026-08-03';
  const dateFilter = searchParams.get('dateFilter') || 'Today';

  const [startDate, setStartDate] = useState<string>(
      startingDate ? moment(startingDate).startOf('day').format('YYYY-MM-DD HH:mm') : moment().startOf('day').format('YYYY-MM-DD'),
    );
    const [endDate, setEndDate] = useState<string>(
      endingDate ? moment(endingDate).startOf('day').format('YYYY-MM-DD HH:mm') : moment().endOf('day').format('YYYY-MM-DD'),
    );
    const [selectedDateFilter, setSelectedDateFilter] = useState<string>(dateFilter ?? 'Today');
  

  const currentStatusTextColor = '#000000';
  const navigate = useNavigate();

  const handleAddJobsClick = () => {
    navigate(
      `/data-pipeline/ibpDashboard/gdoTabs?programID=${encodeURIComponent(gdoId)}&programName=${encodeURIComponent(gdoName)}&projectName=${encodeURIComponent(stageName)}&projectId=${encodeURIComponent(projectId)}&tab=2&isEdited=true`,
    );
  };

  const defaultColumnNames = [
    'PLATFORM',
    'JOB NAME',
    'STATUS',
    'SLA STATUS',
    'ACT. START TIME (CST)',
    'ACT. END TIME (CST)',
    'AVG EXEC. TIME',
    'NEXT EXEC. TIME (CST)',
    'TOTAL EXEC. TIME',
    'EXEC. PER DAY',
    'PIPELINE RUN ID',
    'TRIGGER NAME',
    'LAST 30 EXECUTION TREND',
    'JOB TYPE',
    'ORDER DATE',
    'CYCLIC',
    'CONDITION',
    'JOB OWNER',
    'SCHEDULE INFO',
    'SCHEDULER',
    'COMMENTS',
  ];

  /**
   * Classify a single job's SLA status into a category for roll-up.
   * Returns: 'sla_missed' | 'not_started' | 'in_progress' | 'sla_met' | null
   */
  const classifyJobSlaStatus = (slaStatus: string | null | undefined): string | null => {
    const value = String(slaStatus || '').trim().toLowerCase();
    if (!value || value === 'n/a' || value === 'na' || value === '--' || value === 'not scheduled' || value === 'not started') {
      return 'not_started';
    }
    if (value === 'sla missed') return 'sla_missed';
    if (value === 'in progress') return 'in_progress';
    if (value === 'sla met') return 'sla_met';
    if (value === 'scheduled') return 'not_started';
    return null;
  };

  /**
   * Compute the roll-up SLA status for the entire stage based on individual job statuses.
   * Priority order (highest to lowest):
   *   1. SLA Missed — any job missed SLA
   *   2. Not Started — no jobs begun, or mix of In Progress + Not Started
   *   3. In Progress — at least one executing, none Not Started or SLA Missed
   *   4. SLA Met — all completed, no misses
   */
  const computeRollupStatus = (rows: any[]): string => {
    if (!rows.length) return 'N/A';

    let hasSlaMissed = false;
    let hasNotStarted = false;
    let hasInProgress = false;
    let hasSlaMet = false;

    for (const row of rows) {
      // Skip child/dependent rows — only roll up parent jobs
      if (row.isChildren) continue;
      const category = classifyJobSlaStatus(row.slaStatus);
      if (category === 'sla_missed') hasSlaMissed = true;
      else if (category === 'not_started') hasNotStarted = true;
      else if (category === 'in_progress') hasInProgress = true;
      else if (category === 'sla_met') hasSlaMet = true;
    }

    // Priority per the roll-up spec (same as the backend):
    // SLA Missed > Not Started > In Progress > SLA Met
    if (hasSlaMissed) return 'SLA MISSED';
    if (hasNotStarted) return 'NOT STARTED';
    if (hasInProgress) return 'IN PROGRESS';
    if (hasSlaMet) return 'SLA MET';
    return 'N/A';
  };

  const normalizeStatus = (status: string | null | undefined) => {
    const value = String(status || '').trim();
    if (!value || value === 'N/A' || value === '--') return 'N/A';
    const normalized = value.toLowerCase();
    if (
      normalized === 'sla met' || normalized === 'sla met' ||
      normalized === 'sla missed' ||
      normalized === 'not started' ||
      normalized === 'in progress' ||
      normalized === 'scheduled'
    ) {
      return value.toUpperCase();
    }
    return 'N/A';
  };

  const renderCurrentStatusValue = (status: string) => {
    const normalized = normalizeStatus(status);
    if (normalized === 'N/A') {
      return (
        <Typography variant="bodyValue" lineHeight="24px" sx={{ color: currentStatusTextColor, fontSize: '1rem', fontWeight: 500 }}>
          {normalized}
        </Typography>
      );
    }

    // Match the exact colors used by the STATUS column
    let chipBg = '#B0B7C3';
    if (normalized === 'SLA MET') chipBg = 'rgba(1, 192, 8, 1)';
    else if (normalized === 'SLA MISSED') chipBg = 'rgba(189, 29, 64, 1)';
    else if (normalized === 'IN PROGRESS') chipBg = 'rgba(236, 185, 20, 1)';
    else if (normalized === 'NOT STARTED' || normalized === 'SCHEDULED' || normalized === 'NOT SCHEDULED') chipBg = 'rgba(169, 173, 203, 1)';

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1.5,
          py: 0.5,
          borderRadius: '4px',
          backgroundColor: chipBg,
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: '0.75rem',
          lineHeight: 1.2,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}
      >
        {normalized}
      </Box>
    );
  };

  const [stageKpiData, setStageKpiData] = useState<any>(null);
  const [detailsKpiData, setDetailsKpiData] = useState<any>({});
  const [loadingKpi, setLoadingKpi] = useState(true);
  const [detailsRows, setDetailsRows] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const didInitialKpiLoadRef = useRef(false);
  const detailsRequestIdRef = useRef(0);
  const searchDebounceRef = useRef<number | null>(null);
  const isPageLoading = loadingKpi || loadingDetails;
  // Prefer the backend job→stage roll-up passed via navigation state from
  // gdo-details (row.slaStatus / row.executionStatus are already rolled up);
  // fall back to the client-side roll-up of the job rows on direct loads.
  const stageState = (location.state || null) as any;
  const currentStatusValue = stageState?.slaStatus
    ? normalizeStatus(stageState.slaStatus)
    : normalizeStatus(computeRollupStatus(detailsRows));

  const stageInfo = useMemo(
    () => [
      { label: 'Stage Name', value: stageName },
      {
        label: 'Current Status',
        value: renderCurrentStatusValue(currentStatusValue),
      },
    ],
    [dateFilter, endingDate, startingDate, stageName, currentStatusValue],
  );

  const stageSummary = useMemo(() => {
    const kpiData = detailsKpiData || {};
    const totalJobs = stageKpiData?.totalJobCount ?? 0;

    return [
      { title: 'Total Jobs', value: totalJobs, color: 'totalStages' },
      { title: 'Scheduled', value: kpiData.scheduled ?? 0, color: 'scheduled' },
      { title: 'Failed To Start', value: kpiData.failedToStart ?? 0, color: 'failedToStart' },
      { title: 'Delayed Start', value: kpiData.delayStart ?? 0, color: 'delayedStart' },
      { title: 'Long Running', value: kpiData.longRunning ?? kpiData.inProgress ?? 0, color: 'inProgress' },
      { title: 'Failed', value: kpiData.failed ?? 0, color: 'slaMissed' },
      { title: 'Success', value: kpiData.success ?? 0, color: 'slaMet' },
    ];
  }, [detailsKpiData, stageKpiData]);

  const getCardTextColors = (title: string) => ({ label: '#FFFFFF', value: '#FFFFFF' });

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [clickedItem, setClickedItem] = useState<string | null>(null);
  const [jobSchedulesList, setjobSchedulesList] = useState<Row[]>([]);
  const platformId = jobSchedulesList.length > 0 ? jobSchedulesList[0].platformId : 0;

  const getWidgetClassName = (title: string): string => {
    if (hoveredItem !== title && clickedItem !== title) {
      return 'none';
    }

    switch (title) {
      case 'Total Jobs':
        return 'totalStages';
      case 'Success':
        return 'slaMet';
      case 'Failed':
        return 'slaMissed';
      case 'Failed To Start':
        return 'failedToStart';
      case 'Delayed Start':
        return 'delayedStart';
      case 'Long Running':
        return 'inProgress';
      case 'Scheduled':
        return 'scheduled';
      default:
        return 'none';
    }
  };

  const getJobStatusFilterValue = (title: string) => {
    if (title === 'Scheduled') {
      if (detailsKpiData?.scheduled != null) return 'Scheduled';
      if (detailsKpiData?.notScheduled != null) return 'Not Scheduled';
    }
    return title;
  };

  const handleMouseEnter = (title: string, value: any) => {
    if (value) {
      setHoveredItem(title);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleWidgetClick = (title: string, value: any) => {
    if (!value) return;
    const jobs = stageKpiData?.jobList || stageKpiData?.jobs || [];
    setClickedItem(title);
    setPage(1);

    if (title === 'Total Jobs') {
      const nextFilterInfo = { ...filterInfo };
      if (nextFilterInfo.jobStatus) {
        delete nextFilterInfo.jobStatus;
      }
      setFilterInfo(nextFilterInfo);
      void fetchDetails(jobs, nextFilterInfo, sortingInfo);
      return;
    }

    const filterStatus = getJobStatusFilterValue(title);
    const nextFilterInfo = { ...filterInfo, jobStatus: [filterStatus] };
    setFilterInfo(nextFilterInfo);
    void fetchDetails(jobs, nextFilterInfo, sortingInfo);
  };

  const stageId = (params as any).stageId || (params as any).projectId || '';

  const [filterDataOptions, setFilterDataOptions] = useState<any>({});
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0, currentPage: 1 });
  // Grid filter/sort state to enable header filter UI
  const [sortingInfo, setSortingInfo] = useState<any>({ sortBy: '', sortDir: '' });
  const [filterInfo, setFilterInfo] = useState<any>({});
  const [searchDataValues, setSearchDataValues] = useState<any>({});
   const [dependentInfoLoading, setDependentInfoLoading] = React.useState<boolean>();
   const [selectedJobOption, setSelectedJobOption] = useState<string>('predecessor');
   const [curRow, setCurRow] = useState({} as any);
   const [isCyclicExeData, setIsCyclicExeData] = useState<boolean>(false);
   const [openModal, setOpenModal] = useState<any>({ isOpen: false });

   const [controller] = usePepwiseAuthController();
   const { role } = controller;
   const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User', 'PwC_Edit']);

   const getDropdownOptions = () => {
    return [
      { value: 'predecessor', label: 'Show Predecessor', isDisabled: false },
      { value: 'task', label: 'Show Task', isDisabled: false }
    ];
  };

  // Dropdown change
  const handleHeaderDropdownChange = (value: string) => {
    setSelectedJobOption(value);
  };

  useEffect(() => {
      setSelectedJobOption(platformId === 11 ? 'task' : 'predecessor');
    }, [platformId]);

  const getIbpDateRange = () => {
    const savedRangeType = sessionStorage.getItem('IBPDateRangeType') || 'today';
    const savedTimeZone = sessionStorage.getItem('IBPDateRangeTimeZone') || 'CST';
    const savedStartDate = sessionStorage.getItem('IBPStartDate');
    const savedEndDate = sessionStorage.getItem('IBPEndDate');
    const ianaTimezone = TIMEZONE_MAP[savedTimeZone] || TIMEZONE_MAP.CST;

    const dateRange: any = {
      dateTimeRangeType: savedRangeType,
      timeZone: savedTimeZone,
    };

    if (savedRangeType === 'custom' && savedStartDate && savedEndDate) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(savedStartDate)) {
        dateRange.startTime = moment.tz(savedStartDate, 'YYYY-MM-DD', ianaTimezone).startOf('day').format('DD-MM-YYYY HH:mm:ss');
      } else {
        dateRange.startTime = moment(savedStartDate).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(savedEndDate)) {
        dateRange.endTime = moment.tz(savedEndDate, 'YYYY-MM-DD', ianaTimezone).endOf('day').format('DD-MM-YYYY HH:mm:ss');
      } else {
        dateRange.endTime = moment(savedEndDate).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
      }
    } else {
      const range = calculateRange(savedRangeType, ianaTimezone);
      if (range?.from && range?.to) {
        dateRange.startTime = moment(range.from).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
        dateRange.endTime = savedRangeType === 'today'
          ? moment.tz(ianaTimezone).endOf('day').format('DD-MM-YYYY HH:mm:ss')
          : moment(range.to).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
      } else {
        dateRange.dateTimeRangeType = 'today';
        dateRange.startTime = moment.tz(ianaTimezone).startOf('day').format('DD-MM-YYYY HH:mm:ss');
        dateRange.endTime = moment.tz(ianaTimezone).endOf('day').format('DD-MM-YYYY HH:mm:ss');
      }
    }

    return dateRange;
  };

  const getIbpDateRangeDates = () => {
    const savedRangeType = sessionStorage.getItem('IBPDateRangeType') || 'today';
    const savedTimeZone = sessionStorage.getItem('IBPDateRangeTimeZone') || 'CST';
    const savedStartDate = sessionStorage.getItem('IBPStartDate');
    const savedEndDate = sessionStorage.getItem('IBPEndDate');
    const ianaTimezone = TIMEZONE_MAP[savedTimeZone] || TIMEZONE_MAP.CST;

    if (savedRangeType === 'custom' && savedStartDate && savedEndDate) {
      const sd = /^\d{4}-\d{2}-\d{2}$/.test(savedStartDate)
        ? moment.tz(savedStartDate, 'YYYY-MM-DD', ianaTimezone)
        : moment(savedStartDate).tz(ianaTimezone);
      const ed = /^\d{4}-\d{2}-\d{2}$/.test(savedEndDate)
        ? moment.tz(savedEndDate, 'YYYY-MM-DD', ianaTimezone)
        : moment(savedEndDate).tz(ianaTimezone);
      return {
        startDate: sd.format('YYYY-MM-DD'),
        endDate: ed.format('YYYY-MM-DD'),
      };
    }

    const range = calculateRange(savedRangeType, ianaTimezone);
    if (range?.from && range?.to) {
      return {
        startDate: moment(range.from).tz(ianaTimezone).format('YYYY-MM-DD'),
        endDate: moment(range.to).tz(ianaTimezone).format('YYYY-MM-DD'),
      };
    }

    const today = moment.tz(ianaTimezone).format('YYYY-MM-DD');
    return { startDate: today, endDate: today };
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '--';
    return moment.utc(value).tz('America/Chicago').format('DD-MM-YYYY hh:mm A');
  };

  const loadStageKpi = useCallback(async (searchTextValue: string = filterText) => {
    if (!stageId) {
      setLoadingDetails(false);
      return;
    }
    setLoadingKpi(true);
    try {
      const payload = {
        projectId: stageId,
        projectName: stageName,
        dateRange: getIbpDateRange(),
        searchText: searchTextValue.trim(),
      };
      const resp = await getStageKpi(payload);
      const data = resp?.data ?? resp;
      setStageKpiData(data);
      return data;
    } catch (err) {
      setStageKpiData(null);
      return null;
    } finally {
      setLoadingKpi(false);
    }
  }, [stageId, stageName, filterText]);

  const normalizeJobFilterValue = (value: any) => {
    if (!value) return value;

    if (typeof value === 'string') {
      const jobInfo: any = jobSchedulesList.find(
        (job: any) => job.jobName === value || job.jobId === value || job.name === value,
      );
      return {
        id: jobInfo?.jobId ?? jobInfo?.jobName ?? value,
        platformId: jobInfo?.platformId ?? 0,
        name: jobInfo?.jobName ?? jobInfo?.name ?? value,
      };
    }

    return value;
  };

  const normalizeJobNameFilter = (jobNameFilter: any[]) => {
    return jobNameFilter.map(normalizeJobFilterValue);
  };

  const parseDetailsFilters = (filterState?: any) => {
    const filters: any = {};
    const source = filterState || filterInfo;
    if (source?.jobName?.length) filters.jobName = normalizeJobNameFilter(source.jobName);
    if (source?.jobStatus?.length) filters.jobStatus = source.jobStatus;
    if (source?.platform?.length) filters.platform = source.platform;
    return filters;
  };

  const getIbpJobExecutionPayload = (
    jobs: any[] = [],
    filterState?: any,
    sortingState?: any,
    currentPage: number = page,
    searchTextValue: string = filterText,
  ) => {
    const { startDate: sDate, endDate: eDate } = getIbpDateRangeDates();
    const filterOptions = parseDetailsFilters(filterState);
    return {
      projectId: stageId,
      page: String(currentPage),
      size: String(pageSize),
      fromDate: `${sDate} 00:00`,
      toDate: `${eDate} 23:59`,
      searchText: searchTextValue.trim(),
      ...(Object.keys(filterOptions).length ? { filterOptions } : {}),
      IS24Hrs: false,
      sortBy: sortingState?.sortBy ?? sortingInfo.sortBy ?? '',
      sortDir: sortingState?.sortDir ?? sortingInfo.sortDir ?? '',
      jobList: jobs,
    };
  };

  const handleExportJobExecutions = useCallback(async () => {
    try {
      const jobs = stageKpiData?.jobList || stageKpiData?.jobs || [];
      const payload = getIbpJobExecutionPayload(
        jobs,
        filterInfo,
        sortingInfo,
        1,
        filterText,
      );

      await exportIbpJobExecution({
        ...payload,
        isExport: true,
        isIbp: true,
        size: String(jobs.length || pageSize),
      });
    } catch (error) {
      console.error('Failed to export IBP job executions', error);
    }
  }, [filterInfo, filterText, pageSize, sortingInfo, stageKpiData, stageId]);

  const fetchDetails = useCallback(async (
    jobs: any[] = [],
    filterState?: any,
    sortingState?: any,
    searchTextValue: string = filterText,
  ) => {
    if (!stageId) return;
    const requestId = ++detailsRequestIdRef.current;
    setLoadingDetails(true);
    try {
      const payload = getIbpJobExecutionPayload(jobs, filterState, sortingState, page, searchTextValue);
      const resp = await getIbpJobExecutionData(payload);
      if (requestId !== detailsRequestIdRef.current) return;

      const data = resp?.data ?? resp;
      const results = data?.reqResult || data?.data?.reqResult || [];
      const normalizedRows = results.map((datum: any, index: number) => ({ ...datum, id: index + 1 }));

      setDetailsRows(normalizedRows);
      setjobSchedulesList(normalizedRows);
      setDetailsKpiData(data?.kpiData || {});
      if (data?.filterOptions) setFilterDataOptions(data.filterOptions);

      const pageInfo = data?.page || data?.data?.page;
      if (pageInfo) {
        setPaginationData({
          totalElements: pageInfo.totalElements || results.length,
          totalPages: pageInfo.totalPages || 1,
          currentPage: pageInfo.currentPage || 1,
        });
      } else {
        setPaginationData({ totalElements: results.length, totalPages: Math.ceil(results.length / pageSize) || 1, currentPage: 1 });
      }
    } catch (err) {
      if (requestId !== detailsRequestIdRef.current) return;
      setDetailsRows([]);
      setjobSchedulesList([]);
      setDetailsKpiData({});
    } finally {
      if (requestId === detailsRequestIdRef.current) {
        setLoadingDetails(false);
      }
    }
  }, [stageId, page, pageSize, filterInfo, sortingInfo, filterText]);

  // initial KPI load is done after we initialize local date state from sessionStorage

  // On mount: if URL contains startingDate/endingDate/dateFilter (forward navigation), apply them to sessionStorage
  useEffect(() => {
    try {
      const sd = searchParams.get('startingDate');
      const ed = searchParams.get('endingDate');
      const df = searchParams.get('dateFilter');
      if (sd && ed) {
        // write canonical storage values
        if (df && df.toLowerCase() === 'custom') {
          sessionStorage.setItem('IBPDateRangeType', 'custom');
        } else if (df && df === 'Today') {
          sessionStorage.setItem('IBPDateRangeType', 'today');
        } else if (df) {
          sessionStorage.setItem('IBPDateRangeType', df);
        }
        sessionStorage.setItem('IBPDateRangeTimeZone', 'CST');
        sessionStorage.setItem('IBPStartDate', sd);
        sessionStorage.setItem('IBPEndDate', ed);
        setStartDate(sd ? moment(sd).format('YYYY-MM-DD') : sd);
        setEndDate(ed ? moment(ed).format('YYYY-MM-DD') : ed);
        setSelectedDateFilter(df ?? 'Today');
        // notify listeners (header etc.)
        window.dispatchEvent(new CustomEvent('IBPDateRangeApplied', { detail: { type: df, start: sd, end: ed } }));
        window.dispatchEvent(new Event('ibp-session-storage-updated'));
        // Load KPI and details immediately when URL params are provided
        if (!didInitialKpiLoadRef.current) {
          didInitialKpiLoadRef.current = true;
          (async () => {
            const kpiData = await loadStageKpi();
            const jobs = kpiData?.jobList || kpiData?.jobs || [];
            await fetchDetails(jobs);
          })();
        }
        return;
      }
    } catch (err) {
      // ignore
    }

    // If no URL params, initialize from sessionStorage (preserve forward selection)
    const { startDate: sDate, endDate: eDate } = getIbpDateRangeDates();
    setStartDate(sDate);
    setEndDate(eDate);
    const savedRangeType = sessionStorage.getItem('IBPDateRangeType') || 'today';
    setSelectedDateFilter(savedRangeType === 'today' ? 'Today' : savedRangeType === 'custom' ? 'Custom' : savedRangeType);

    // After initializing date state from sessionStorage, load KPI and then details
    if (!didInitialKpiLoadRef.current) {
      didInitialKpiLoadRef.current = true;
      (async () => {
        const kpiData = await loadStageKpi();
        const jobs = kpiData?.jobList || kpiData?.jobs || [];
        await fetchDetails(jobs);
      })();
    }
  }, []);

  // Watch for IBP date-range changes saved to sessionStorage and refresh data when changed.
  useEffect(() => {
    const keys = ['IBPDateRangeType', 'IBPStartDate', 'IBPEndDate', 'IBPDateRangeTimeZone'];
    const lastValuesRef = { vals: keys.map((k) => sessionStorage.getItem(k)) };

    const checkForChanges = async () => {
      const current = keys.map((k) => sessionStorage.getItem(k));
      const changed = current.some((v, i) => v !== lastValuesRef.vals[i]);
      if (!changed) return;

      lastValuesRef.vals = current;
      // update local display states
      const dates = getIbpDateRangeDates();
      setStartDate(dates.startDate);
      setEndDate(dates.endDate);
      const savedRangeType = sessionStorage.getItem('IBPDateRangeType') || 'today';
      setSelectedDateFilter(savedRangeType === 'today' ? 'Today' : savedRangeType === 'custom' ? 'Custom' : savedRangeType);

      // reload KPI then details using refreshed KPI jobs
      const refreshedKpi = await loadStageKpi();
      const jobs = refreshedKpi?.jobList || refreshedKpi?.jobs || [];
      await fetchDetails(jobs);
    };

    const interval = window.setInterval(() => {
      void checkForChanges();
    }, 500);
    return () => window.clearInterval(interval);
  }, [fetchDetails, loadStageKpi]);

  const handleGridSorting = (sorting: any) => {
    const nextSorting = sorting || { sortBy: '', sortDir: '' };

    if (nextSorting?.sortBy === 'jobStatus') {
      setSortingInfo({ sortBy: '', sortDir: '' });
      return;
    }

    setSortingInfo(nextSorting);
    void fetchDetails(stageKpiData?.jobList || stageKpiData?.jobs || [], filterInfo, nextSorting);
  };

  const handleGridFilter = (newFilter: any, columnKey?: any) => {
    const clonedFilter = newFilter ? structuredClone(newFilter) : {};
    setFilterInfo(clonedFilter);
    setPage(1);

    // If Clear All Filters was triggered from the grid, reset any active widget selection
    const isClearAll =
      clonedFilter &&
      Object.values(clonedFilter).every((v: any) => Array.isArray(v) && v.length === 0);
    if (isClearAll) {
      setClickedItem(null);
    }

    void fetchDetails(stageKpiData?.jobList || stageKpiData?.jobs || [], clonedFilter, sortingInfo);
  };

  const handleFilterSearch = (searchValue: any, columnKey?: any) => {
    if (!searchValue || !columnKey) return;
    const currentOptions = filterDataOptions[columnKey] || [];
    const filteredOptions = currentOptions.filter((option: any) =>
      String(option).toLowerCase().includes(searchValue.toLowerCase())
    );
    setSearchDataValues({ name: filteredOptions });
  };

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setFilterText(nextValue);
    setPage(1);

    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
    }

    if (!stageKpiData) return;

    searchDebounceRef.current = window.setTimeout(() => {
      void loadStageKpi(nextValue);
      void fetchDetails(
        stageKpiData?.jobList || stageKpiData?.jobs || [],
        filterInfo,
        sortingInfo,
        nextValue,
      );
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const passInfoToParent = (_data: any) => {
    void loadStageKpi();
  };

  const handleEditClose = (closeType: string) => {
    setOpenModal({
      isOpen: false,
    });
    if (closeType === 'updated') {
      void loadStageKpi();
    }
  };

  const stageTooltipItems = useMemo(
    () => [
      { label: 'Data Owner', value: stageKpiData?.dataOwner || '-' },
      { label: 'Data Steward', value: stageKpiData?.dataSteward || '-' },
      { label: 'Created At', value: formatDateTime(stageKpiData?.createdAt) },
      { label: 'Updated At', value: formatDateTime(stageKpiData?.updatedAt) },
    ],
    [stageKpiData],
  );

  const stageInfoTooltip = (
    <Tooltip
      arrow
      placement="bottom-start"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: '#EAF4FA !important',
            color: '#0B3A56',
            borderRadius: '8px',
            border: '1px solid rgba(11,58,86,0.12)',
            boxShadow: '0px 10px 24px rgba(13, 43, 67, 0.18)',
            px: 1.5,
            py: 1,
            minWidth: '220px',
            '& .MuiTooltip-arrow': {
              color: '#EAF4FA !important',
            },
          },
        },
      }}
      title={
        <Box sx={{ display: 'grid', gap: 0.75 }}>
          {stageTooltipItems.map((item) => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: '220px', lineHeight: 1.4 }}>
              <Box sx={{ width: 4, height: 4, minWidth: 4, borderRadius: '50%', backgroundColor: '#000000', mt: '0.45rem' }} />
              <Typography sx={{ color: '#000000', fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.5 }}>
                {item.label}: <Box component="span" sx={{ color: '#000000', fontWeight: 500 }}>{item.value}</Box>
              </Typography>
            </Box>
          ))}
        </Box>
      }
    >
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
        <InfoGreyIcon />
      </Box>
    </Tooltip>
  );

  const handleDetailsThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDetailsThreeDotsClose = (_event: unknown, _reason?: string) => undefined;

  const handleAction = (action: string) => {
    // placeholder for menu actions
  };

  const platformIconMap: Record<number, string> = {
    1: TeradataPlatformIcon,
    2: BODSPlatformIcon,
    3: DDHPlatformIcon,
    4: AzureDataFactoryPlatformIcon,
    7: MDTPlatformIcon,
    8: WindowsServerPlatformIcon,
    9: SAPBWPlatformIcon,
    10: SQLPlatformIcon,
    11: ADBPlatformIcon,
  };

  const getPlatformIcon = (platformId?: number) => {
    const iconSrc = platformId && platformIconMap[platformId]
      ? platformIconMap[platformId]
      : TeradataPlatformIcon;

    return iconSrc;
  };

  const platformIdFormatter = () => {
    return ({ row }: any) => {
      let platformLabel = ' - ';

      if (row.application === 'SAPS4H_AME') {
        platformLabel = 'S4';
      } else if (row.application === 'SAPENTHANA') {
        platformLabel = 'EHANA';
      } else if (row.platformId) {
        platformLabel = PLATFORM_ID_TO_NAME[row.platformId] || 'Teradata';
      }

      const iconSrc = getPlatformIcon(row.platformId);

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box
            component="img"
            src={iconSrc}
            alt={platformLabel}
            sx={{
              width: 20,
              height: 20,
              objectFit: 'contain',
              flexShrink: 0,
              display: 'block',
            }}
          />
          <RowTypography sx={{ whiteSpace: 'nowrap' }}>{platformLabel}</RowTypography>
        </Box>
      );
    };
  };

  let constRows: any = [];
  let setLocalData = false;

  type ExpandLogicParams = {
    row: any;
    rowIndex: number;
    newRows: Row[];
    projectId: number;
    startDate: any;
    endDate: any;
    resourceTypes?: any;
    name: number;
  };

  const expandLogic = async ({
      row,
      rowIndex,
      newRows,
      projectId,
      startDate,
      endDate,
      resourceTypes,
      name,
    }: ExpandLogicParams) => {
      const boxRows: any = localStorage.getItem('depJobExeData');
      const storageRows = boxRows ? JSON.parse(boxRows) : [];
      let hasStrRow = false;
      let children: Row[] = [];
      let childRows: Row[] = [];
      if (!setLocalData) {
        storageRows.filter((strRow: any) => {
          if (
            strRow.jobId == row.jobId &&
            (row.exeId ? strRow.exeId == row.exeId : strRow.oDate == row.oDate) &&
            strRow.projectId == Number(projectId)
          ) {
            hasStrRow = true;
            newRows.splice(Number(rowIndex) + 1, 0, ...strRow.children);
            newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded, children: strRow.children };
            const newUpdatedRows = [...newRows];
            constRows = newUpdatedRows;
            setjobSchedulesList(newUpdatedRows);
            setDetailsRows(newUpdatedRows);
          }
        });
        if (!hasStrRow) {
          let payload = jobSchedulesList.filter((data) => data.id === name);
  
          setDependentInfoLoading(true);
          const res = await getDependentJobExeData({
            ...payload[0],
            startDate,
            endDate,
            selectedJobOption
          });
          setLocalData = true;
          setDependentInfoLoading(false);
                    if (res.success) {
            res.data['dependInfo'] = res.data.depDataResult;

            Object.entries(res.data).forEach(([key, value]: any) => {
              childRows = dependInfoTransformData(value);
            });
            children = childRows.map((x: any) => {
              return { ...x, isChildren: true, slaStatus: null };
            });
            newRows.splice(Number(rowIndex) + 1, 0, ...children);
            newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded, children };
            const newUpdatedRows = [...newRows];
  
            constRows = newUpdatedRows;
            setjobSchedulesList(newUpdatedRows);
            setDetailsRows(newUpdatedRows);  
            const stBoxRows: any = localStorage.getItem('depJobExeData');
            const tempRows = [
              {
                jobId: row.jobId,
                oDate: row.oDate,
                exeId: row.exeId || '',
                projectId: Number(projectId),
                children: children,
              },
            ];
            if (stBoxRows) {
              let tempStorageBoxRows = JSON.parse(stBoxRows);
              tempStorageBoxRows = tempStorageBoxRows.concat(tempRows);
              localStorage.setItem('depJobExeData', JSON.stringify(tempStorageBoxRows));
            } else {
              localStorage.setItem('depJobExeData', JSON.stringify(tempRows));
            }
          }
        }
        if (resourceTypes?.length > 0) {
          childRows = resourceTypes;
        }
        children = childRows.map((x: any) => {
          return { ...x, isChildren: true, slaStatus: null };
        });
        newRows.splice(Number(rowIndex) + 1, 0, ...children);
        newRows[rowIndex] = { ...row, isExpanded: true, children };
      }
    };

  async function toggleSubRow(name: number, resourceTypes: any, eleLevel: string) {
    const rows = constRows.length ? constRows : jobSchedulesList;
    const rowIndex = rows.findIndex((r: any) => r.id === name);
    const row = rows[rowIndex];
    const newRows = [...rows];

    if (!row.isExpanded) {
      expandLogic({
        name,
        resourceTypes,
        row,
        rowIndex,
        newRows,
        projectId: Number(projectId),
        startDate,
        endDate,
      });
      return;
    }

    let children = row.children ?? [];
    newRows.splice(Number(rowIndex) + 1, children.length);
    newRows[rowIndex] = { ...row, isExpanded: false };
    const newUpdatedRows = [...newRows];

    constRows = newUpdatedRows;
    setjobSchedulesList(newUpdatedRows);
    setDetailsRows(newUpdatedRows);
    row.isExpanded = false;
  }

  const jobNameFormatter = (
    toggleSubRow: (id: number, dependentData: any, eleLevel: string) => void,
  ) => {
    return ({ row, isCellSelected }: any) => {
      const hasChildren = Boolean(row.hasDependent);
  
      const isHWA =
        String(row.platform || '')
          .toLowerCase()
          .endsWith('hwa');
  
      const showExpander = isHWA
        ? hasChildren
        : (hasChildren || (row.platformId !== 10 && row.lineageJobRunId));
  
      const className = [
        row.className,
        row.eleLevel,
        row.jobStatus,
        row.isChildren ? 'child-view' : 'parent-view',
        hasChildren ? 'has-children' : 'single',
      ].join(' ');
  
      return (
        <div className={className}>
          {showExpander && (
            <CellExpanderFormatter
              isCellSelected={isCellSelected}
              expanded={row.isExpanded === true}
              onCellExpand={() => toggleSubRow(row.id, row.dependentData, row.eleLevel)}
            />
          )}
          {'   '}
          <RowTypography isSuccessor={!row.dependentData} title={row.jobName}>
            {row.jobName ?? '--'}
          </RowTypography>
        </div>
      );
    };
  };

  const handleSaveComment = async (
      text: string,
      jobId: any,
      jobExeId: any,
      projectId: any,
      platformId: any,
      rowData: any,
    ): Promise<void> => {
      try {
        const response = await postJobComment(projectId, jobExeId, jobId, text, platformId, rowData);
        if (response?.success) {
          await fetchDetails(stageKpiData?.jobList || stageKpiData?.jobs || [], filterInfo, sortingInfo);
        }
      } catch (error) {
        console.error('Error updating comment:', error);
      }
    };

  const renderEditableCell = (
    props: any,
    isAllowed: boolean,
    onSave: (
      text: string,
      jobId: any,
      jobExeId: any,
      projectId: any,
      platformId: any,
      rowData: any
    ) => Promise<void>
  ) => {
    const { row } = props;

    return (
      <EditableCell
        jobId={row.jobId}
        jobExeId={row.jobExeId || row.jobRunId}
        projectId={row.projectId}
        comment={row.comment}
        platformId={row.platformId}
        isAllowed={isAllowed}
        textColor={theme.palette.textLight.main}
        onSave={onSave}
        rowData={row}
      />
    );
  };

  const fetchCyclicExecutions = (row: any) => {
    setCurRow({ ...row });
    setIsCyclicExeData(true);
  };

  function getColumns(): Array<Column<Row>> {
    const columns: Array<Column<Row>> = [];

    columns.push(
      {
        key: 'platformId',
        name: 'PLATFORM',
        formatter: platformIdFormatter(),
        minWidth: 120,
        frozen: true,
      },
      {
        key: 'jobName',
        name: 'JOB NAME',
        formatter: jobNameFormatter(toggleSubRow),
        minWidth: 260,
        frozen: true,
      },
      {
        key: 'jobStatus',
        name: 'STATUS',
        sortable: false,
        formatter: ({ row }: any) => {
          const status = row.jobStatus || row.jobStatusText || '--';
          const chipStyle = (() => {
            const normalized = String(status).toLowerCase();
            if (normalized.includes('failed to start'))
              return { backgroundColor: 'rgba(160, 129, 255, 1)', color: '#FFFFFF' };
            if (normalized.includes('delayed start'))
              return { backgroundColor: 'rgba(255, 155, 93, 1)', color: '#FFFFFF' };
            if (normalized.includes('success'))
              return { backgroundColor: 'rgba(1, 192, 8, 1)', color: '#FFFFFF' };
            if (normalized.includes('failed'))
              return { backgroundColor: 'rgba(189, 29, 64, 1)', color: '#FFFFFF' };
            if (normalized.includes('scheduled'))
              return { backgroundColor: 'rgba(169, 173, 203, 1)', color: '#FFFFFF' };
            if (normalized.includes('in progress') || normalized.includes('long running'))
              return { backgroundColor: 'rgba(236, 185, 20, 1)', color: '#FFFFFF' };
            return { backgroundColor: '#B0B7C3', color: '#FFFFFF' };
          })();
          if (status === '--' || status === 'NA')
            return <Box sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{status}</Box>;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                minHeight: '24px',
                px: 1.5,
                py: 0.5,
                borderRadius: '4px',
                backgroundColor: chipStyle.backgroundColor,
                color: chipStyle.color,
                fontWeight: 700,
                fontSize: '0.68rem',
                lineHeight: 1.2,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
              }}
            >
              {status}
            </Box>
          );
        },
        minWidth: 140,
        frozen: true,
      },
      {
        key: 'slaStatus',
        name: 'SLA STATUS',
        sortable: false,
        formatter: slaStatusFormatter(),
        minWidth: 140,
        frozen: true,
      },
    );

    // Time based (custom vs ADF)
    if (platformId === 4) {
      columns.push(
        {
          key: 'actualStartTime',
          name: 'ACT. START TIME (CST)',
          formatter: actualStartTimeFormatter(),
          minWidth: 200,
        },
        {
          key: 'actualEndTime',
          name: 'ACT. END TIME (CST)',
          formatter: actualEndTimeFormatter(),
          minWidth: 200,
        },
        {
          key: 'avgRunTime',
          name: 'AVG EXEC. TIME',
          formatter: avgRunTimeFormatter(),
          minWidth: 130,
        },
      );
    }

    if ([1, 2, 3, 7, 8, 9, 10, 11].includes(platformId)) {
      columns.push(
        {
          key: 'actualStartTime',
          name: 'ACT. START TIME (CST)',
          formatter: actualStartTimeFormatterCustom(),
          minWidth: 200,
        },
        {
          key: 'actualEndTime',
          name: 'ACT. END TIME (CST)',
          formatter: actualEndTimeFormatterCustom(),
          minWidth: 200,
        },
        {
          key: 'avgRunTime',
          name: 'AVG EXEC. TIME',
          formatter: avgRunTimeFormatterCustom(),
          minWidth: 130,
        },
      );
    }

    columns.push(
      {
        key: 'nextExecutionTime',
        name: 'NEXT EXEC. TIME (CST)',
        formatter: ({ row }: any) => (
          <RowTypography sx={{ textAlign: 'center' }}>
            {row.nextExecutionTime
              ? moment.utc(row.nextExecutionTime).tz('America/Chicago').format('DD-MM-YYYY hh:mm A')
              : '--'}
          </RowTypography>
        ),
        minWidth: 200,
      },
    );

    if (platformId === 4) {
      columns.push(
        {
          key: 'totalExecutionTime',
          name: 'TOTAL EXEC. TIME',
          formatter: totalExecutionTimeFormatter(),
          minWidth: 130,
        },
        {
          key: 'totalExecutions',
          name: 'EXEC. PER DAY',
          formatter: totalExecutionsFormatter(fetchCyclicExecutions),
          minWidth: 160,
        },
        {
          key: 'jobRunId',
          name: 'PIPELINE RUN ID',
          formatter: jobRunIdFormatter(),
          minWidth: 150,
      },
      );
    }

    if ([1, 2, 3, 7, 8, 9, 10, 11].includes(platformId)) {
      columns.push(
        {
          key: 'totalExecutionTime',
          name: 'TOTAL EXEC. TIME',
          formatter: totalExecutionTimeFormatter(),
          minWidth: 130,
        },
        {
          key: 'totalExecutions',
          name: 'EXEC. PER DAY',
          formatter: totalExecutionsFormatterCustom(fetchCyclicExecutions),
          minWidth: 160,
        },
        {
        key: 'pipeline_trigger_id',
        name: 'PIPELINE RUN ID',
        formatter: ({ row }: any) => (
          <RowTypography sx={{ textAlign: 'center' }}>
            {row.pipeline_trigger_id || '--'}
          </RowTypography>
        ),
        minWidth: 200,
      },
      );
    }

    // Folder platforms
        if ([1, 2, 3, 7].includes(platformId)) {
          columns.push(
            {
              key: 'folderNames',
              name: 'Folder Name',
              formatter: folderNamesFormatter(),
              minWidth: 130,
            },
            {
              key: 'subFolderNames',
              name: 'Subfolder Name',
              formatter: subFolderNamesFormatter(),
              minWidth: 130,
            },
          );
        }
    
        // ADF extra
        if (platformId === 4) {
          columns.push(
            {
              key: 'dataAssetName',
              name: 'Data Asset Name',
              formatter: dataAssetNameFormatter(),
              minWidth: 130,
            },
            {
              key: 'silverTableName',
              name: 'Silver Table Name',
              formatter: silverTableNameFormatter(),
              minWidth: 130,
            },
          );
        }

    columns.push(
      {
        key: 'triggerName',
        name: 'TRIGGER NAME',
        formatter: triggerNameFormatter(),
        minWidth: 140,
      },
      {
        key: 'execStatusTrend',
        name: 'LAST 30 EXECUTION TREND',
        formatter: ragBarFormatter(),
        minWidth: 220,
      },
      {
        key: 'jobType',
        name: 'JOB TYPE',
        formatter: ({ row }: any) => (
          <RowTypography sx={{ textAlign: 'center' }}>
            {row.jobTypeName || row.jobType || '--'}
          </RowTypography>
        ),
        minWidth: 120,
      },
      {
        key: 'oDate',
        name: 'ORDER DATE',
        formatter: orderDateFormatter(),
        minWidth: 140,
      },
      {
        key: 'isCyclic',
        name: 'CYCLIC',
        formatter: isCyclicFormatter(),
        minWidth: 100,
      },
      {
        key: 'jobCondition',
        name: 'CONDITION',
        formatter: jobConditionFormatter(),
        minWidth: 120,
      },
      {
        key: 'jobOwner',
        name: 'JOB OWNER',
        formatter: ({ row }: any) => (
          <RowTypography sx={{ textAlign: 'left' }}>
            {row.jobOwner || row.jobOwnerEmail || '--'}
          </RowTypography>
        ),
        minWidth: 200,
      },
      {
        key: 'scheduleInfo',
        name: 'SCHEDULE INFO',
        formatter: scheduleInfoFormatter(),
        minWidth: 320,
      },
      {
        key: 'schedulerName',
        name: 'SCHEDULER',
        formatter: ({ row }: any) => (
          <RowTypography sx={{ textAlign: 'center' }}>{row.schedulerName || '--'}</RowTypography>
        ),
        minWidth: 160,
      },
      {
        key: 'comment',
        name: 'COMMENTS',
        resizable: false,
        minWidth: 200,
        formatter(props: any) {
          return renderEditableCell(props, isAllowed, handleSaveComment);
        },
        cellClass: 'highlight-cell',
      },
    );
    return columns;
  }

  return (
    <PageWrapper>
      <Box className="main-bordered-container" bgcolor="backgroundLight.primary">
        <PageHeader
          icon={DashboardIcon}
          title={[
            {
              text: 'All GDOs',
              link: '/data-pipeline/programOps/IBP/markets/ibpDashboard',
            },
            {
              text: gdoName,
              link: `/data-pipeline/ibpDashboard/gdo-details/${encodeURIComponent(gdoId)}/${encodeURIComponent(gdoName)}`,
            },
            {
              text: stageName,
            },
          ]}
        >
          <Button variant="pepwiseSecondary" startIcon={<AddIcon />} onClick={handleAddJobsClick}>
            Add Jobs
          </Button>
        </PageHeader>

        <Dialog open={openModal.isOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md">
          <DataPipelineModal
            handleEditClose={handleEditClose}
            programData={openModal}
            passInfoToParent={passInfoToParent}
          />
        </Dialog>

        <Drawer anchor="bottom" open={isCyclicExeData} onClose={() => setIsCyclicExeData(false)}>
          <CyclicExecutions
            startDate={startDate}
            endDate={endDate}
            curRow={curRow}
            setIsCyclicExeData={setIsCyclicExeData}
          />
        </Drawer>
        {/* <Drawer anchor="bottom" open={slaHistory} onClose={() => setSlaHistory(false)}>
                  <SLAHistory setSlaHistory={setSlaHistory} proInfo={proInfo} />
                </Drawer> */}

        <Box p={3} pr={2}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <DataInfoSecondary
                bgColor="#fff"
                sx={{
                  border: '1px solid #E8EEF4',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  borderRadius: '8px',
                }}
                infoCards={stageInfo.map((item) => ({
                  label: item.label,
                  value: item.value,
                  labelProps: { sx: { color: '#71758F', fontSize: '0.8rem', fontWeight: 600 } },
                  valueProps: {
                    sx: { color: '#0B3A56', fontSize: '1rem', fontWeight: 500, lineHeight: '24px' },
                  },
                }))}
                infoTooltip={stageInfoTooltip}
                infoOptionMenu={{
                  onClick: handleDetailsThreeDotsClick,
                  onClose: handleDetailsThreeDotsClose,
                  menuItems: [
                    {
                      key: 'edit',
                      label: 'Edit',
                      icon: <EditIcon />,
                      onMenuClick: () => handleAction('edit'),
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: <DeleteIcon />,
                      onMenuClick: () => handleAction('delete'),
                    },
                  ],
                }}
                isAdmin={true}
              />
            </Box>

            <Box sx={{ flex: '1 1 420px', minWidth: 0, overflowX: 'auto' }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', alignItems: 'stretch' }}>
                {stageSummary.map(({ title, value, color }) => {
                  const displayTitle = title;
                  const textColors = getCardTextColors(title);
                  const isActive = hoveredItem === title || clickedItem === title;
                  const activeColor = (colorsDark.boxColor as any)[color as any];

                  return (
                    <Box
                      key={title}
                      sx={{
                        flex: '1 1 calc(14.285% - 8px)',
                        minWidth: {
                          xs: 'calc(50% - 8px)',
                          sm: 'calc(33.333% - 8px)',
                          lg: 'calc(14.285% - 8px)',
                        },
                        display: 'flex',
                      }}
                      className="DataInfo"
                      onMouseEnter={() => handleMouseEnter(title, value)}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => handleWidgetClick(title, value)}
                    >
                      <DataInfoColorWidget
                        title={displayTitle}
                        value={value}
                        boxColor={color as any}
                        isActive={isActive}
                        isLoading={isPageLoading}
                        className={getWidgetClassName(title)}
                        onClick={() => handleWidgetClick(title, value)}
                        labelProps={{
                          sx: {
                            color: isActive && activeColor ? activeColor.bgColor : textColors.label,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                          },
                        }}
                        valueProps={{
                          sx: {
                            color: isActive && activeColor ? activeColor.bgColor : textColors.value,
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            lineHeight: '24px',
                          },
                        }}
                        sx={{
                          border: '1px solid #E8EEF4',
                          boxShadow: isActive ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.04)',
                          backgroundColor: isActive ? '#FFFFFF' : activeColor?.bgColor,
                          borderBottom:
                            isActive && activeColor
                              ? `7px solid ${activeColor.bgColor}`
                              : undefined,
                          '& .colorIntimation': { display: 'none' },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            {isPageLoading && <Loader />}
            {!isPageLoading && dependentInfoLoading && <Loader />}
            {!isPageLoading && !dependentInfoLoading && (
              <CustomizedDataGrid
                tableColumns={getColumns()}
                tableData={detailsRows}
                tableType="ibp-gdo-stage-job-details"
                defaultColumnNames={defaultColumnNames}
                onSorting={handleGridSorting}
                currSorting={sortingInfo}
                filterDataOptions={filterDataOptions}
                searchDataValues={searchDataValues}
                onFilterCheck={handleGridFilter}
                currFilter={filterInfo}
                onFilterSearch={handleFilterSearch}
                onFilterClick={() => {}}
                page={page}
                pageSize={pageSize}
                paginationData={paginationData}
                listLength={detailsRows.length}
                isFetching={isPageLoading}
                isRenderPagination={true}
                isShowMoreButton={true}
                showExport={true}
                exportTableData={handleExportJobExecutions}
                onCellClick={() => {}}
                dropdownOptions={getDropdownOptions()}
                dropdownValue={selectedJobOption}
                onDropdownChange={handleHeaderDropdownChange}
                isChildren={true}
                childrenContainerProps={{ sx: { width: '100%', justifyContent: 'flex-end' } }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="overlayWidgetTitle"
                    sx={{ color: `${theme.palette.table.title} !important`, whiteSpace: 'nowrap' }}
                    component="span"
                  >
                    Jobs
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <CustomTextField
                      placeholder="Search by Platform, Job, Trigger Name"
                      value={filterText}
                      onChange={handleSearchTextChange}
                      style={{ width: '22rem', borderRadius: '4px', zIndex: '99' }}
                      sx={{
                        flexShrink: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F6FBFD',
                          borderRadius: '4px',
                          '& fieldset': { borderColor: '#6F8A99' },
                          '&:hover fieldset': { borderColor: '#6F8A99' },
                          '&.Mui-focused fieldset': { borderColor: '#6F8A99' },
                        },
                        '& .MuiInputBase-input': {
                          color: '#0B3A56',
                          padding: '14px',
                          paddingLeft: 0,
                        },
                        '& .MuiInputBase-input::placeholder': { color: '#6F8A99', opacity: 1 },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: '#6F8A99' }} />
                          </InputAdornment>
                        ),
                        sx: { height: 36 },
                      }}
                    />
                  </Box>
                </Box>
              </CustomizedDataGrid>
            )}
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
};

export default StageDetails;
